/**
 * Settings Page Layout Component
 *
 * Provides a consistent layout for settings pages with built-in
 * loading and access control patterns.
 *
 * Design inspired by Linear and modern SaaS settings patterns:
 * - Centered content with constrained max-width
 * - Generous horizontal padding
 * - Single-column vertical layout
 */

import { AccessRestricted } from './AccessRestricted';

import { FoldingCube } from '@/components/common';

interface SettingsPageLayoutProps {
  title: string;
  description: string;
  children: React.ReactNode;
  isLoading?: boolean;
  isOwner?: boolean;
  requiresOwner?: boolean;
}

export function SettingsPageLayout({
  title,
  description,
  children,
  isLoading = false,
  isOwner = true,
  requiresOwner = false,
}: SettingsPageLayoutProps) {
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <FoldingCube />
      </div>
    );
  }

  if (requiresOwner && !isOwner) {
    return <AccessRestricted />;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Scrollable container */}
      <div className="flex-1 overflow-y-auto">
        {/* Centered content with max-width */}
        <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          </div>
          {/* Content */}
          <div className="space-y-6">{children}</div>
        </div>
      </div>
    </div>
  );
}
